import { apiCall, useApi } from "@excellent/ui/api";
import type { VerificationWorkflowDto } from "@excellent/ui/api/contracts/verification-define";
import { useVerificationEnvOptional } from "../env-provider";
import type { VerificationEnvironment } from "../env";
import { verificationCanonicalClients } from "../api/canonical-clients";
import { environmentActor, errorMessage } from "./workflows-contracts";

export type WorkflowRow = VerificationWorkflowDto;

type WorkflowActionResult =
  | { ok: true; workflow: WorkflowRow; message: string; createdId?: string | null }
  | {
      ok: false;
      message: string;
      reason: "environment-missing" | "workflow-update-api-unmounted" | "oracle-not-independent" | "api-error";
      createdId?: string | null;
    };

export function useWorkflowActions(environmentOverride?: VerificationEnvironment | null) {
  const env = useVerificationEnvOptional();
  const environment: VerificationEnvironment | null = environmentOverride ?? env?.environment ?? null;
  const ready = env ? env.ready && env.environment !== null : true;
  const workflowsApi = useApi(
    verificationCanonicalClients.workflowsList.operation,
    environment && ready ? { query: { env: environment } } : null,
  );

  const readOnly = (message: string): WorkflowActionResult => ({
    ok: false,
    message,
    reason: environment ? "workflow-update-api-unmounted" : "environment-missing",
    createdId: null,
  });

  return {
    environment,
    ready,
    workflowsApi,
    workflows: workflowsApi.data?.items ?? [],
    createWorkflow: async (input: { name: string; actor: string; oracle: string; evidence?: string; doc?: string }) => {
      if (!environment) return readOnly("The active Environment must load before creating a workflow.");
      try {
        const result = await apiCall(verificationCanonicalClients.workflowsIntake.operation, {
          query: { env: environment },
          body: {
            actorId: environmentActor(environment),
            name: input.name,
            doc: input.doc ?? input.evidence ?? input.name,
            actor: input.actor,
            oracle: input.oracle,
            verifier: "independent-source",
            evidence: input.evidence ?? input.oracle,
          },
        });
        await workflowsApi.mutate();
        return {
          ok: true,
          workflow: result.workflow,
          message: `Created ${result.workflow.name}.`,
          createdId: result.workflow.id,
        };
      } catch (err) {
        return {
          ok: false,
          message: errorMessage(err),
          reason: "api-error",
          createdId: null,
        };
      }
    },
    bindOracle: (_workflowId: string, _oracle: string) =>
      readOnly("Oracle binding on an existing workflow needs a workflow update contract."),
    admitWorkflow: async (workflowId: string) => {
      if (!environment) return readOnly("The active Environment must load before admitting a workflow.");
      try {
        const result = await apiCall(verificationCanonicalClients.workflowsAdmit.operation, {
          params: { id: workflowId },
          query: { env: environment },
          body: { actorId: environmentActor(environment) },
        });
        await workflowsApi.mutate();
        return {
          ok: true,
          workflow: result.workflow,
          message: `Admitted ${result.workflow.name}.`,
          createdId: result.workflow.id,
        };
      } catch (err) {
        return {
          ok: false,
          message: errorMessage(err),
          reason: "api-error",
          createdId: null,
        };
      }
    },
  };
}
